import React, { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { api, formatErr } from "@/lib/api";
import { publicImageUrl } from "@/components/ImageUpload";
import { toast } from "sonner";

/**
 * Produces a cropped PNG Blob from an image URL + a react-easy-crop `croppedAreaPixels`.
 */
async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height,
  );
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
}

export default function BannerCropper({ open, onOpenChange, sourcePath, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPx, setAreaPx] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => setAreaPx(areaPixels), []);

  const reset = () => { setCrop({ x: 0, y: 0 }); setZoom(1); };

  const save = async () => {
    if (!areaPx || !sourcePath) return;
    setSaving(true);
    try {
      const srcUrl = publicImageUrl(sourcePath);
      const blob = await getCroppedBlob(srcUrl, areaPx);
      const form = new FormData();
      form.append("file", blob, "banner.png");
      const { data } = await api.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      const newPath = data.storage_path || data.path;
      onCropped(newPath);
      toast.success("Banner repositioned");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not save crop");
    } finally {
      setSaving(false);
    }
  };

  if (!sourcePath) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="banner-cropper" className="rounded-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl font-light tracking-tight">
            Reposition your photo
          </DialogTitle>
          <p className="text-sm text-neutral-600 pt-1">
            Drag the photo to choose what shows. Pinch or use the slider to zoom in.
          </p>
        </DialogHeader>

        <div className="relative w-full bg-neutral-100" style={{ height: 320 }}>
          <Cropper
            image={publicImageUrl(sourcePath)}
            crop={crop}
            zoom={zoom}
            aspect={5 / 2}
            showGrid={true}
            restrictPosition={true}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="cover"
            minZoom={1}
            maxZoom={4}
          />
        </div>

        <div className="px-1 pt-3">
          <div className="flex items-center gap-4">
            <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 w-12">Zoom</span>
            <Slider
              value={[zoom]}
              onValueChange={(v) => setZoom(v[0])}
              min={1}
              max={4}
              step={0.05}
              className="flex-1"
              data-testid="crop-zoom"
            />
          </div>
        </div>

        <DialogFooter className="mt-2 flex items-center justify-between">
          <Button variant="ghost" onClick={reset} data-testid="crop-reset" className="rounded-sm">Reset</Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving || !areaPx} data-testid="crop-save" className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
              {saving ? "Saving…" : "Save crop"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
